import React, { useEffect, lazy, Suspense, Component } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { PublicSessionProvider } from './contexts/PublicSessionContext'
import { ApCourseProvider } from './contexts/ApCourseContext'
import { StudyWorkspaceProvider } from './contexts/StudyWorkspaceContext'
import ErrorBoundary from './components/ErrorBoundary'
import FullAuthShell from './components/FullAuthShell'
import MembershipPage from './pages/MembershipPage'
import OnboardingPage from './pages/OnboardingPage'
import ProtectedAppShell from './components/ProtectedAppShell'
import SignIn from './components/SignIn'
import SignUp from './components/SignUp'
import { performAuthStateCleanup } from './utils/authStateCleanup'
import { loadLazyModule } from './lib/lazyModuleLoader'
import { getDomainMigrationRedirect } from './lib/domainMigration'
import {
  DEFAULT_STUDY_ROUTE,
  getCanonicalStudyRouteFromPath,
  getStudyRoute,
} from './lib/studyNavigation'

const createLazyPage = (moduleId, loader) => lazy(() => loadLazyModule(loader, { moduleId }));

// Lazy load all components
const HomePage = createLazyPage('HomePage', () => import('./pages/HomePage'))
const PrivacyPolicy = createLazyPage('PrivacyPolicy', () => import('./pages/PrivacyPolicy'))
const TermsOfService = createLazyPage('TermsOfService', () => import('./pages/TermsOfService'))
const PricingBasicPage = createLazyPage('PricingBasicPage', () => import('./pages/PricingBasicPage'))
const PricingProPage = createLazyPage('PricingProPage', () => import('./pages/PricingProPage'))
const PricingProUltraPage = createLazyPage('PricingProUltraPage', () => import('./pages/PricingProUltraPage'))
const AppHomePage = createLazyPage('AppHomePage', () => import('./pages/AppHomePage'))
const ApTutorPage = createLazyPage('ApTutorPage', () => import('./pages/ApTutorPage'))
const ApFlashcardsPage = createLazyPage('ApFlashcardsPage', () => import('./pages/ApFlashcardsPage'))
const ApCourseMapPage = createLazyPage('ApCourseMapPage', () => import('./pages/ApCourseMapPage'))
const ApSettingsPage = createLazyPage('ApSettingsPage', () => import('./pages/ApSettingsPage'))
const FlashcardDeckPage = createLazyPage('FlashcardDeckPage', () => import('./pages/FlashcardDeckRoute'))
const PostPaymentSignup = createLazyPage('PostPaymentSignup', () => import('./components/PostPaymentSignup'))
const AuthCallback = createLazyPage('AuthCallback', () => import('./components/AuthCallback'))
const PaymentSuccessPage = createLazyPage('PaymentSuccessPage', () => import('./pages/PaymentSuccessPage'))
// Subscription Management page
const ManageSubscription = createLazyPage('ManageSubscription', () => import('./pages/ManageSubscriptionRoute'));
const UpdatePaymentMethod = createLazyPage('UpdatePaymentMethod', () => import('./pages/UpdatePaymentMethodRoute'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="h-screen flex items-center justify-center bg-background">
    <div className="relative h-12 w-12">
      <span className="absolute inset-0 block rounded-full bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 opacity-70 animate-ping"></span>
      <span className="absolute inset-1 block rounded-full bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700"></span>
    </div>
  </div>
);

const StudyCompatibilityRedirect = () => {
  const { tab } = useParams();
  const location = useLocation();
  const redirectPath = getStudyRoute(tab);

  return (
    <Navigate
      to={`${redirectPath}${location.search}${location.hash}`}
      replace
    />
  );
};

const AppRoutes = () => {
  const location = useLocation();
  const canonicalStudyPath = getCanonicalStudyRouteFromPath(location.pathname);
  const domainRedirect = getDomainMigrationRedirect({
    hostname: window.location.hostname,
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
  });
  useEffect(() => {
    if (domainRedirect) window.location.replace(domainRedirect);
  }, [domainRedirect]);

  if (domainRedirect) return <LoadingFallback />;

  const renderProtectedElement = (element) => (
    <Suspense fallback={<LoadingFallback />}>
      <ProtectedAppShell>{element}</ProtectedAppShell>
    </Suspense>
  );

  const renderFullAuthElement = (element) => (
    <Suspense fallback={<LoadingFallback />}>
      <FullAuthShell>{element}</FullAuthShell>
    </Suspense>
  );

  if (canonicalStudyPath && canonicalStudyPath !== location.pathname) {
    return (
      <Navigate
        to={`${canonicalStudyPath}${location.search}${location.hash}`}
        replace
      />
    );
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/signin" element={<SignIn />} />
      <Route path="/membership" element={renderFullAuthElement(<MembershipPage />)} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/free-trial/*" element={<Navigate to={`/${location.search}${location.hash}`} replace />} />
      <Route path="/pricing/basic" element={<PricingBasicPage />} />
      <Route path="/pricing/pro" element={<PricingProPage />} />
      <Route path="/pricing/pro-ultra" element={<PricingProUltraPage />} />
      <Route path="/post-payment-signup" element={<PostPaymentSignup />} />
      <Route path="/payment-success" element={renderFullAuthElement(<PaymentSuccessPage />)} />
      <Route path="/auth/callback" element={renderFullAuthElement(<AuthCallback />)} />
      <Route path="/onboarding" element={renderFullAuthElement(<OnboardingPage />)} />
      <Route path="/home" element={renderProtectedElement(<AppHomePage />)} />
      <Route path="/ai-tutor" element={renderProtectedElement(<ApTutorPage />)} />
      <Route path="/flashcards" element={renderProtectedElement(<ApFlashcardsPage />)} />
      <Route path="/course-map" element={renderProtectedElement(<ApCourseMapPage />)} />
      <Route path="/settings" element={renderProtectedElement(<ApSettingsPage />)} />
      <Route path="/revision-tracker" element={<Navigate to="/course-map" replace />} />
      <Route path="/manage-subscription" element={renderProtectedElement(<ManageSubscription />)} />
      <Route path="/manage-subscription/update-card" element={renderProtectedElement(<UpdatePaymentMethod />)} />
      <Route path="/flashcards/:slug" element={renderProtectedElement(<FlashcardDeckPage />)} />
      <Route path="/study" element={<Navigate to={DEFAULT_STUDY_ROUTE} replace />} />
      <Route path="/study/:tab" element={<StudyCompatibilityRedirect />} />
      {/* Fallback route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Add a custom ErrorBoundary component to handle auth errors
class AuthErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // IGNORE sign-out related errors - these are expected and normal
    if (error && error.message) {
      const errorMessage = error.message.toLowerCase();
      const isSignOutError =
        errorMessage.includes('auth session missing') ||
        errorMessage.includes('session not found') ||
        errorMessage.includes('user not found') ||
        errorMessage.includes('invalid session') ||
        errorMessage.includes('no session') ||
        errorMessage.includes('session expired') ||
        errorMessage.includes('signout') ||
        errorMessage.includes('sign out') ||
        errorMessage.includes('logout') ||
        errorMessage.includes('log out');

      if (isSignOutError) {
        console.log('[AuthErrorBoundary] Ignoring sign-out related error:', errorMessage);
        // Don't trigger error boundary for sign-out errors
        return { hasError: false };
      }
    }

    // Update state so the next render will show the fallback UI for other errors
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Check if this is a sign-out error that should be ignored
    if (error && error.message) {
      const errorMessage = error.message.toLowerCase();
      const isSignOutError =
        errorMessage.includes('auth session missing') ||
        errorMessage.includes('session not found') ||
        errorMessage.includes('user not found') ||
        errorMessage.includes('invalid session') ||
        errorMessage.includes('no session') ||
        errorMessage.includes('session expired') ||
        errorMessage.includes('signout') ||
        errorMessage.includes('sign out') ||
        errorMessage.includes('logout') ||
        errorMessage.includes('log out');

      if (isSignOutError) {
        console.log('[AuthErrorBoundary] componentDidCatch: Ignoring sign-out related error:', errorMessage);
        return; // Don't process sign-out errors
      }
    }

    // Log non-signout errors to console
    console.error("App Error Boundary caught an error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReset = () => {
    // Clear Supabase auth token from localStorage to force re-login
    try {
      localStorage.removeItem('supabase.auth.token');
      console.log("Removed auth token from localStorage");

      // Clear any auth check flags
      window.__AUTH_CHECK_IN_PROGRESS = false;

      // Reset the error state
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null
      });

      // Force page reload
      window.location.reload();
    } catch (error) {
      console.error("Error during reset:", error);
    }
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div className="error-boundary flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h2>
            <p className="mb-6 text-gray-700">We encountered an error while loading this page.</p>
            <div className="mb-4">
              <details className="bg-gray-100 p-2 rounded">
                <summary className="cursor-pointer text-sm text-gray-600">Error details (for technical support)</summary>
                <pre className="mt-2 text-xs overflow-auto max-h-40 p-2 bg-gray-200 rounded">
                  {this.state.error?.toString() || "Unknown error"}
                </pre>
              </details>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={this.handleReset}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Reset & Reload
              </button>
              <a
                href="/"
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Go to Home
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {
  useEffect(() => {
    // Perform one-time auth state cleanup to fix sign-out issues
    // from previous error monitoring system
    try {
      performAuthStateCleanup();
    } catch (cleanupError) {
      console.warn('[App.jsx] Non-critical auth cleanup error:', cleanupError);
      // Don't block app initialization
    }

  }, []);

  return (
    <AuthErrorBoundary>
      <AuthProvider>
        <PublicSessionProvider>
          <ApCourseProvider>
            <StudyWorkspaceProvider>
              <Router>
                <ErrorBoundary>
                  <Suspense fallback={<LoadingFallback />}>
                    <AppRoutes />
                  </Suspense>
                </ErrorBoundary>
              </Router>
            </StudyWorkspaceProvider>
          </ApCourseProvider>
        </PublicSessionProvider>
      </AuthProvider>
    </AuthErrorBoundary>
  )
}

export default App 
